import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { CheckSquare, Square, Info } from 'lucide-react';

// Constants for dropdown options
const WARM_UP_OPTIONS = [
  'As per rules',
  '2 minutes',
  '3 minutes',
  '4 minutes',
  '5 minutes'
];

const FIGURE_DRAW_OPTIONS = [
  'Figure Draw Not Required',
  'Standard Draw',
  'Standard Draw issued as part of event notice',
  'Draw Group, 2 figures',
  'Draw Group, 2 figures excluding loops'
];

const TWO_PART_OPTIONS = [
  'Skate Round One ONLY',
  'Skate Both Rounds',
  'Skate Round Two ONLY',
  'No events with two rounds'
];

export function ScheduleSettingsDialog({ 
  open, 
  onOpenChange, 
  competition,
  onComplete,
  readOnly = false,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableGrades, setAvailableGrades] = useState(null);
  
  // Track selections - keyed by grade_id
  const [mainSelections, setMainSelections] = useState({});
  const [subSelections, setSubSelections] = useState({});
  
  // Event management settings. ``figureDrawType`` default depends on
  // the comp tier (set in the effect below once we know the comp):
  //   Local/Regional → "Standard Draw issued as part of event notice"
  //   Anything else   → "Standard Draw"
  // Both are only meaningful when the comp actually includes Figures.
  const [warmUpOption, setWarmUpOption] = useState('As per rules');
  const [figureDrawType, setFigureDrawType] = useState('Figure Draw Not Required');
  const [twoPartEvents, setTwoPartEvents] = useState('Skate Both Rounds');
  const [useRollart, setUseRollart] = useState(true);
  const [beginnerCompOnly, setBeginnerCompOnly] = useState(false);
  const [showLoopWarning, setShowLoopWarning] = useState(false);

  // Fetch available grades when dialog opens
  useEffect(() => {
    if (open && competition?.competition_id) {
      fetchAvailableGrades();
    }
  }, [open, competition?.competition_id]);

  const fetchAvailableGrades = async () => {
    setLoading(true);
    try {
      // Fetch available grades
      const gradesRes = await axios.get(
        `${API}/competitions/${competition.competition_id}/available-grades`,
        { withCredentials: true }
      );
      setAvailableGrades(gradesRes.data);
      
      // Fetch existing schedule settings (if any)
      const settingsRes = await axios.get(
        `${API}/competitions/${competition.competition_id}/schedule-settings`,
        { withCredentials: true }
      );
      
      const existingSelections = settingsRes.data.selections || [];
      const existingSettings = settingsRes.data.event_settings;
      const hasExistingSettings = existingSelections.length > 0;
      
      // Build selection maps from existing data or use pre_selected defaults
      const mainSel = {};
      const subSel = {};
      
      if (hasExistingSettings) {
        // Use existing selections
        existingSelections.forEach(sel => {
          if (sel.schedule_type === 'main') {
            mainSel[sel.grade_id] = sel.selected;
          } else if (sel.schedule_type === 'sub') {
            subSel[sel.grade_id] = sel.selected;
          }
        });
        
        // Ensure all grades have a selection value (in case new grades were added)
        gradesRes.data.main_schedule_grades.forEach(grade => {
          if (!(grade.grade_id in mainSel)) {
            mainSel[grade.grade_id] = grade.pre_selected;
          }
        });
        gradesRes.data.sub_schedule_grades.forEach(grade => {
          if (!(grade.grade_id in subSel)) {
            subSel[grade.grade_id] = grade.pre_selected;
          }
        });
        
        // Load existing event settings
        if (existingSettings) {
          setWarmUpOption(existingSettings.warm_up_option || 'As per rules');
          // Choose default if the value was never set (existing comps
          // upgraded into this feature). Local/Regional → event-notice
          // draw; everything else → plain Standard Draw.
          let drawDefault = 'Figure Draw Not Required';
          if (competition?.figure_events_included) {
            const tier = (competition?.competition_type || '').toLowerCase();
            const isLocalRegional = tier === 'local' || tier === 'regional' || tier === 'invitational';
            drawDefault = isLocalRegional
              ? 'Standard Draw issued as part of event notice'
              : 'Standard Draw';
          }
          setFigureDrawType(existingSettings.figure_draw_type || drawDefault);
          setTwoPartEvents(existingSettings.two_part_events || 'Skate Both Rounds');
          setUseRollart(existingSettings.use_rollart !== false);
          setBeginnerCompOnly(existingSettings.beginner_comp_only || false);
        }
      } else {
        // Initialize selections based on pre_selected flags (new competition)
        gradesRes.data.main_schedule_grades.forEach(grade => {
          mainSel[grade.grade_id] = grade.pre_selected;
        });
        gradesRes.data.sub_schedule_grades.forEach(grade => {
          subSel[grade.grade_id] = grade.pre_selected;
        });
        // Sensible figure-draw default for a brand-new comp (Feb 2026
        // owner request): Local/Regional gets the Event-Notice draw,
        // Area+ gets a plain Standard Draw. Hosts can still change it
        // afterwards; this just stops them landing on the wrong option.
        if (competition?.figure_events_included) {
          const tier = (competition?.competition_type || '').toLowerCase();
          const isLocalRegional = tier === 'local' || tier === 'regional' || tier === 'invitational';
          setFigureDrawType(
            isLocalRegional
              ? 'Standard Draw issued as part of event notice'
              : 'Standard Draw'
          );
        }
      }
      
      setMainSelections(mainSel);
      setSubSelections(subSel);
      
    } catch (error) {
      console.error('Error fetching available grades:', error);
      toast.error('Failed to load schedule settings');
    } finally {
      setLoading(false);
    }
  };

  // Group grades for display - handles both discipline grouping and grade-level grouping
  const groupGrades = (grades) => {
    if (!grades) return { byDiscipline: {}, byGrade: {} };
    
    const byDiscipline = {};
    const byGrade = {};
    
    grades.forEach(grade => {
      if (grade.group_by_grade) {
        // Group by grade name (e.g., "Beginner 1")
        const gradeName = grade.name || 'Unknown';
        if (!byGrade[gradeName]) byGrade[gradeName] = [];
        byGrade[gradeName].push(grade);
      } else {
        // Group by discipline (default behavior)
        const disc = grade.discipline_name || 'Unknown';
        if (!byDiscipline[disc]) byDiscipline[disc] = [];
        byDiscipline[disc].push(grade);
      }
    });
    
    return { byDiscipline, byGrade };
  };
  
  const mainGradeGroups = useMemo(() => {
    return groupGrades(availableGrades?.main_schedule_grades);
  }, [availableGrades]);

  const subGradeGroups = useMemo(() => {
    return groupGrades(availableGrades?.sub_schedule_grades);
  }, [availableGrades]);

  // Keep these for backward compatibility with toggle functions
  const groupedMainGrades = mainGradeGroups.byDiscipline;
  const groupedSubGrades = subGradeGroups.byDiscipline;

  const toggleMainSelection = (gradeId) => {
    if (readOnly) return;
    setMainSelections(prev => ({
      ...prev,
      [gradeId]: !prev[gradeId]
    }));
  };

  const toggleSubSelection = (gradeId) => {
    if (readOnly) return;
    setSubSelections(prev => ({
      ...prev,
      [gradeId]: !prev[gradeId]
    }));
  };

  // Handle beginner competition toggle - auto-untick non-local/regional grades
  const handleBeginnerCompChange = (isBeginnerComp) => {
    if (readOnly) return;
    setBeginnerCompOnly(isBeginnerComp);
    
    if (isBeginnerComp && availableGrades) {
      // Untick any grades that are NOT local_regional_only
      const newMainSelections = { ...mainSelections };
      availableGrades.main_schedule_grades.forEach(grade => {
        if (!grade.local_regional_only) {
          newMainSelections[grade.grade_id] = false;
        }
      });
      setMainSelections(newMainSelections);
      
      // Also update sub selections if applicable
      if (availableGrades.sub_schedule_grades) {
        const newSubSelections = { ...subSelections };
        availableGrades.sub_schedule_grades.forEach(grade => {
          if (!grade.local_regional_only) {
            newSubSelections[grade.grade_id] = false;
          }
        });
        setSubSelections(newSubSelections);
      }
    }
  };

  // Check if any selected grades are loop events
  const hasSelectedLoopGrades = useMemo(() => {
    if (!availableGrades) return false;
    
    const allGrades = [
      ...(availableGrades.main_schedule_grades || []),
      ...(availableGrades.sub_schedule_grades || [])
    ];
    
    const allSelections = { ...mainSelections, ...subSelections };
    
    return allGrades.some(grade => {
      const isSelected = allSelections[grade.grade_id];
      const isLoopGrade = (grade.discipline_name || '').toLowerCase().includes('loop');
      return isSelected && isLoopGrade;
    });
  }, [availableGrades, mainSelections, subSelections]);

  // Handle figure draw type change with loop warning
  const handleFigureDrawTypeChange = (newValue) => {
    if (readOnly) return;
    setFigureDrawType(newValue);
    
    // Check if selecting "excluding loops" and there are loop grades selected
    if (newValue === 'Draw Group, 2 figures excluding loops' && hasSelectedLoopGrades) {
      setShowLoopWarning(true);
    }
  };

  const toggleAllInGroup = (groupKey, groupType, isMain = true) => {
    if (readOnly) return;
    // groupType is either 'discipline' or 'grade'
    const gradeGroups = isMain ? mainGradeGroups : subGradeGroups;
    const grades = groupType === 'discipline' 
      ? gradeGroups.byDiscipline[groupKey] 
      : gradeGroups.byGrade[groupKey];
    const selections = isMain ? mainSelections : subSelections;
    const setSelections = isMain ? setMainSelections : setSubSelections;
    
    if (!grades) return;
    
    // Check if all are currently selected
    const allSelected = grades.every(g => selections[g.grade_id]);
    
    // Toggle all
    const newSelections = { ...selections };
    grades.forEach(g => {
      newSelections[g.grade_id] = !allSelected;
    });
    setSelections(newSelections);
  };

  const handleFinalise = async () => {
    setSaving(true);
    try {
      const mainSelectionsArray = Object.entries(mainSelections).map(([grade_id, selected]) => ({
        grade_id,
        selected
      }));
      
      const subSelectionsArray = availableGrades?.has_sub_schedule 
        ? Object.entries(subSelections).map(([grade_id, selected]) => ({
            grade_id,
            selected
          }))
        : null;

      await axios.post(
        `${API}/competitions/${competition.competition_id}/schedule-settings`,
        {
          main_schedule_selections: mainSelectionsArray,
          sub_schedule_selections: subSelectionsArray,
          warm_up_option: warmUpOption,
          figure_draw_type: figureDrawType,
          two_part_events: twoPartEvents,
          use_rollart: useRollart,
          beginner_comp_only: beginnerCompOnly
        },
        { withCredentials: true }
      );
      
      toast.success('Competition finalised successfully!');
      onOpenChange(false);
      if (onComplete) onComplete();
    } catch (error) {
      console.error('Error saving schedule settings:', error);
      toast.error('Failed to save schedule settings');
    } finally {
      setSaving(false);
    }
  };

  const renderGradeListBox = (gradeGroups, selections, toggleSelection, isMain, title) => {
    const { byDiscipline, byGrade } = gradeGroups;
    const disciplineNames = Object.keys(byDiscipline).sort();
    const gradeNames = Object.keys(byGrade).sort();
    
    if (disciplineNames.length === 0 && gradeNames.length === 0) {
      return (
        <div className="text-center py-4 text-muted-foreground">
          No grades found for this schedule
        </div>
      );
    }

    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-[#2D4A3E] text-white px-3 py-2 font-medium">
          {title}
        </div>
        <div className="max-h-[280px] overflow-y-auto">
          {/* First, render grades grouped by grade name (group_by_grade = true) */}
          {gradeNames.map(gradeName => {
            const grades = byGrade[gradeName];
            const allSelected = grades.every(g => selections[g.grade_id]);
            const someSelected = grades.some(g => selections[g.grade_id]);
            
            return (
              <div key={`grade-${gradeName}`} className="border-b last:border-b-0">
                {/* Grade name header */}
                <div 
                  className="flex items-center gap-2 px-3 py-2 bg-amber-50 cursor-pointer hover:bg-amber-100"
                  onClick={() => toggleAllInGroup(gradeName, 'grade', isMain)}
                >
                  {allSelected ? (
                    <CheckSquare className="h-4 w-4 text-[#2D4A3E]" />
                  ) : someSelected ? (
                    <div className="h-4 w-4 border-2 border-[#2D4A3E] rounded flex items-center justify-center">
                      <div className="h-2 w-2 bg-[#2D4A3E] rounded-sm" />
                    </div>
                  ) : (
                    <Square className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="font-medium text-sm">{gradeName}</span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {grades.filter(g => selections[g.grade_id]).length}/{grades.length}
                  </span>
                </div>
                
                {/* Discipline items under this grade */}
                <div className="divide-y divide-gray-100">
                  {grades.map(grade => (
                    <div
                      key={grade.grade_id}
                      className="flex items-center gap-2 px-3 py-1.5 pl-8 cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleSelection(grade.grade_id)}
                    >
                      {selections[grade.grade_id] ? (
                        <CheckSquare className="h-4 w-4 text-[#2D4A3E]" />
                      ) : (
                        <Square className="h-4 w-4 text-gray-400" />
                      )}
                      <span className="text-sm">{grade.discipline_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          
          {/* Then, render grades grouped by discipline (group_by_grade = false) */}
          {disciplineNames.map(discipline => {
            const grades = byDiscipline[discipline];
            const allSelected = grades.every(g => selections[g.grade_id]);
            const someSelected = grades.some(g => selections[g.grade_id]);
            
            return (
              <div key={`disc-${discipline}`} className="border-b last:border-b-0">
                {/* Discipline header */}
                <div 
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleAllInGroup(discipline, 'discipline', isMain)}
                >
                  {allSelected ? (
                    <CheckSquare className="h-4 w-4 text-[#2D4A3E]" />
                  ) : someSelected ? (
                    <div className="h-4 w-4 border-2 border-[#2D4A3E] rounded flex items-center justify-center">
                      <div className="h-2 w-2 bg-[#2D4A3E] rounded-sm" />
                    </div>
                  ) : (
                    <Square className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="font-medium text-sm">{discipline}</span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {grades.filter(g => selections[g.grade_id]).length}/{grades.length}
                  </span>
                </div>
                
                {/* Grade items */}
                <div className="divide-y divide-gray-100">
                  {grades.map(grade => (
                    <div
                      key={grade.grade_id}
                      className="flex items-center gap-2 px-3 py-1.5 pl-8 cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleSelection(grade.grade_id)}
                    >
                      {selections[grade.grade_id] ? (
                        <CheckSquare className="h-4 w-4 text-[#2D4A3E]" />
                      ) : (
                        <Square className="h-4 w-4 text-gray-400" />
                      )}
                      <span className="text-sm">{grade.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!competition) return null;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {readOnly ? 'Schedule Settings (View Only) - ' : 'Schedule Settings - '}
            {competition.name}
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D4A3E]"></div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-6 pr-2">
            {/* Info banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-3">
              <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Select which events to include in this competition</p>
                <p className="mt-1">Events are pre-selected based on whether Figures and/or Artistic disciplines are included. Uncheck any events that will not be offered at this competition.</p>
              </div>
            </div>
            
            {/* Event Management Settings - Moved to top */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-semibold text-[#2D4A3E]">Event Management Settings</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Beginner Competition Only */}
                <div>
                  <Label className="text-sm font-medium">Beginner Competition with Local/Regional Grades Only?</Label>
                  <div className="flex gap-4 mt-2">
                    <label className={`flex items-center gap-2 ${readOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                      <input
                        type="radio"
                        name="beginnerComp"
                        checked={beginnerCompOnly === true}
                        onChange={() => handleBeginnerCompChange(true)}
                        disabled={readOnly}
                        data-testid="beginner-comp-yes"
                      />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className={`flex items-center gap-2 ${readOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                      <input
                        type="radio"
                        name="beginnerComp"
                        checked={beginnerCompOnly === false}
                        onChange={() => handleBeginnerCompChange(false)}
                        disabled={readOnly}
                        data-testid="beginner-comp-no"
                      />
                      <span className="text-sm">No</span>
                    </label>
                  </div>
                </div>
                
                {/* RollArt */}
                <div>
                  <Label className="text-sm font-medium">Will RollArt be used at this event?</Label>
                  <div className="flex gap-4 mt-2">
                    <label className={`flex items-center gap-2 ${readOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                      <input
                        type="radio"
                        name="rollart"
                        checked={useRollart === true}
                        onChange={() => !readOnly && setUseRollart(true)}
                        disabled={readOnly}
                        data-testid="rollart-yes"
                      />
                      <span className="text-sm">Yes</span>
                    </label>
                    <label className={`flex items-center gap-2 ${readOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                      <input
                        type="radio"
                        name="rollart"
                        checked={useRollart === false}
                        onChange={() => !readOnly && setUseRollart(false)}
                        disabled={readOnly}
                        data-testid="rollart-no"
                      />
                      <span className="text-sm">No</span>
                    </label>
                  </div>
                </div>
                
                {/* Warm Up Options */}
                <div>
                  <Label className="text-sm font-medium">Warm Up Options for Event</Label>
                  <select
                    className="w-full mt-1 border rounded-lg px-3 py-2 text-sm disabled:bg-stone-100 disabled:cursor-not-allowed"
                    value={warmUpOption}
                    onChange={(e) => setWarmUpOption(e.target.value)}
                    disabled={readOnly}
                    data-testid="warm-up-select"
                  >
                    {WARM_UP_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                
                {/* Figure Draw Type (only if Figures included) */}
                {availableGrades?.figure_events_included && (
                  <div>
                    <Label className="text-sm font-medium">Figure Draw Type for Event</Label>
                    <select
                      className="w-full mt-1 border rounded-lg px-3 py-2 text-sm disabled:bg-stone-100 disabled:cursor-not-allowed"
                      value={figureDrawType}
                      onChange={(e) => handleFigureDrawTypeChange(e.target.value)}
                      disabled={readOnly}
                      data-testid="figure-draw-select"
                    >
                      {FIGURE_DRAW_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Two Part Events (only if any grades have round two) */}
                {availableGrades?.has_two_round_events && (
                  <div>
                    <Label className="text-sm font-medium">Two Part Events</Label>
                    <select
                      className="w-full mt-1 border rounded-lg px-3 py-2 text-sm disabled:bg-stone-100 disabled:cursor-not-allowed"
                      value={twoPartEvents}
                      onChange={(e) => setTwoPartEvents(e.target.value)}
                      disabled={readOnly}
                      data-testid="two-part-select"
                    >
                      {TWO_PART_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            
            {/* Main Schedule */}
            {renderGradeListBox(
              mainGradeGroups, 
              mainSelections, 
              toggleMainSelection,
              true,
              `Main Schedule Events`
            )}
            
            {/* Sub Competition Schedule (conditional) */}
            {availableGrades?.has_sub_schedule && (
              <div className="mt-4">
                {renderGradeListBox(
                  subGradeGroups, 
                  subSelections, 
                  toggleSubSelection,
                  false,
                  `Sub Competition Schedule Events (${competition.sub_competition_name || 'Sub Competition'})`
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4 border-t mt-4">
          {readOnly ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="schedule-settings-close-button"
            >
              Close
            </Button>
          ) : (
            <>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button 
                className="btn-primary" 
                onClick={handleFinalise}
                disabled={loading || saving}
                data-testid="finalise-competition-button"
              >
                {saving ? 'Saving...' : 'Finalise Competition'}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Loop Events Warning Dialog - rendered outside parent Dialog for proper z-index */}
    <AlertDialog open={showLoopWarning} onOpenChange={setShowLoopWarning}>
      <AlertDialogContent className="z-[200]">
        <AlertDialogHeader>
          <AlertDialogTitle>Warning - Loop events</AlertDialogTitle>
          <AlertDialogDescription>
            If you're excluding loop events from the standard figure draws, you may also want to 
            remove Loop Grades from the events on offer. To do that, uncheck the grades in the 
            schedule lists above.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => setShowLoopWarning(false)}>
            Understood
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
}
